/**
 * Exports page — data portability surface.
 *
 * Four tabs, matching the backend data-portability API:
 *  - Data Backup: download a machine-readable kalpa-export.zip of selected domains.
 *  - Import: upload a backup, preview (dry-run) it, then apply after a successful preview.
 *  - AI Context Export: privacy-filtered Markdown/txt/JSONL bundle for external AI tools.
 *  - Import History: past import batches with rollback.
 *
 * Distinct from the human-readable habit report export (HabitExportDialog).
 * See docs/60 API.md and docs/60 USER_GUIDE.md.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';

import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type ReflectionMode = 'metadata_only' | 'encrypted' | 'plaintext';

interface DomainDef {
  key: string;
  label: string;
}

// Order/labels for the Data Backup domain checkboxes. Must stay in sync with
// schemas.BACKUP_DOMAINS — a domain missing here is silently absent from every
// backup taken through the UI.
//
// Two calendar tables are deliberately NOT backup domains and so are absent
// here by design: external_event_links is an ownership register (restoring it
// elsewhere would license that installation to patch/delete real Google
// Calendar events), and calendar_sync_journal is this installation's
// operational history. See routers/data_portability.py.
const BACKUP_DOMAINS: DomainDef[] = [
  { key: 'users', label: 'Profile' },
  { key: 'habits', label: 'Habits' },
  { key: 'habit_tags', label: 'Habit tags' },
  { key: 'habit_logs', label: 'Habit logs' },
  { key: 'habit_debt_ledger', label: 'Habit debt ledger' },
  { key: 'habit_check_in_windows', label: 'Check-in windows' },
  { key: 'habit_check_in_options', label: 'Check-in options' },
  { key: 'score_presets', label: 'Score presets' },
  { key: 'score_preset_options', label: 'Score preset answers' },
  { key: 'check_in_events', label: 'Check-in history' },
  { key: 'hour_classes', label: 'Hour classes' },
  { key: 'hour_class_ranges', label: 'Hour class ranges' },
  { key: 'temporal_commitments', label: 'Commitments' },
  { key: 'commitment_occurrences', label: 'Commitment history' },
  { key: 'reflections', label: 'Reflections' },
  { key: 'timers', label: 'Life timers' },
  { key: 'timer_tags', label: 'Timer tags' },
  { key: 'work_timers', label: 'Work timers' },
  { key: 'time_entries', label: 'Time entries' },
  { key: 'time_entry_tags', label: 'Time entry tags' },
  { key: 'planned_events', label: 'Planned events' },
  { key: 'projects', label: 'Projects' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'task_blocks', label: 'Scheduled task blocks' },
  { key: 'timelines', label: 'Timelines' },
  { key: 'milestones', label: 'Milestones' },
  { key: 'integration_metadata', label: 'Integration metadata' },
  { key: 'user_settings', label: 'Saved preferences' },
];

interface DomainReport {
  domain: string;
  read: number;
  valid: number;
  invalid: number;
  to_create: number;
  to_skip: number;
  duplicates: number;
  conflicts: number;
  orphaned_references: number;
  remapped_references: number;
  warnings: string[];
}

interface PreviewResponse {
  ok: boolean;
  would_apply: boolean;
  conflict_mode: string;
  totals: Record<string, number>;
  domains: DomainReport[];
  dependency_warnings: string[];
}

interface BatchResponse {
  id: number;
  status: string;
  filename?: string | null;
  total_created: number;
  total_skipped: number;
  total_failed: number;
  created_at?: string | null;
}

const filenameFromDisposition = (header?: string): string | null => {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch ? plainMatch[1] : null;
};

const triggerBlobDownload = (data: BlobPart, contentType: string, filename: string): void => {
  const blob = new Blob([data], { type: contentType || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const todayYmd = (): string => new Date().toISOString().slice(0, 10);
const thirtyDaysAgoYmd = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return d.toISOString().slice(0, 10);
};

const Exports: React.FC = () => {
  const {
    pageHeaderSx,
    pageGridSpacing,
    cardContentPaddingSx,
    sectionGapSx,
    tableCellPaddingSx,
  } = useLayoutTokens();
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // ------------------------------------------------------------------ Data Backup
  const [selectedDomains, setSelectedDomains] = useState<Record<string, boolean>>(
    () => Object.fromEntries(BACKUP_DOMAINS.map((d) => [d.key, true])),
  );
  const [reflectionMode, setReflectionMode] = useState<ReflectionMode>('encrypted');
  const [includeExternalIds, setIncludeExternalIds] = useState(true);
  const [includeSourceMetadata, setIncludeSourceMetadata] = useState(true);
  const [backupPasscode, setBackupPasscode] = useState('');
  const [backupBusy, setBackupBusy] = useState(false);

  const toggleDomain = (key: string) =>
    setSelectedDomains((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleBackupDownload = async () => {
    setError(null);
    setInfo(null);
    const domains = BACKUP_DOMAINS.filter((d) => selectedDomains[d.key]).map((d) => d.key);
    if (domains.length === 0) {
      setError('Select at least one domain to export.');
      return;
    }
    setBackupBusy(true);
    try {
      const headers: Record<string, string> = {};
      // Any reflection export needs the passcode when one is set — titles are
      // passcode-protected too, not just plaintext bodies.
      if (selectedDomains.reflections && backupPasscode) {
        headers['X-Reflection-Passcode'] = backupPasscode;
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/data-portability/export`,
        {
          domains,
          reflection_content: reflectionMode,
          include_external_ids: includeExternalIds,
          include_source_metadata: includeSourceMetadata,
        },
        { responseType: 'blob', headers },
      );
      triggerBlobDownload(
        res.data,
        res.headers['content-type'] || 'application/zip',
        filenameFromDisposition(res.headers['content-disposition']) || `kalpa-export-${todayYmd()}.zip`,
      );
      setInfo('Backup downloaded.');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to export backup.'));
    } finally {
      setBackupBusy(false);
    }
  };

  // ------------------------------------------------------------------ Import
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [conflictMode, setConflictMode] = useState('skip');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [importBusy, setImportBusy] = useState<'preview' | 'apply' | null>(null);

  const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setImportFile(file);
    setPreview(null); // a new file invalidates any prior preview
    if (event.target) event.target.value = '';
  };

  const runPreview = async () => {
    if (!importFile) return;
    setError(null);
    setInfo(null);
    setImportBusy('preview');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('conflict_mode', conflictMode);
      const res = await axios.post<PreviewResponse>(
        `${API_BASE_URL}/api/data-portability/import/preview`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPreview(res.data);
      setInfo('Preview complete. Review the counts, then apply if they look right.');
    } catch (err: any) {
      setPreview(null);
      setError(getApiErrorMessage(err, 'Import preview failed.'));
    } finally {
      setImportBusy(null);
    }
  };

  const runApply = async () => {
    if (!importFile || !preview?.ok) return;
    setError(null);
    setInfo(null);
    setImportBusy('apply');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('conflict_mode', conflictMode);
      const res = await axios.post<BatchResponse>(
        `${API_BASE_URL}/api/data-portability/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setInfo(
        `Import applied (batch #${res.data.id}): created ${res.data.total_created}, ` +
          `skipped ${res.data.total_skipped}, failed ${res.data.total_failed}.`,
      );
      setPreview(null);
      setImportFile(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Import apply failed.'));
    } finally {
      setImportBusy(null);
    }
  };

  // ------------------------------------------------------------------ AI Context
  const [aiFormat, setAiFormat] = useState('markdown');
  const [aiRange, setAiRange] = useState('30d');
  const [aiStart, setAiStart] = useState(thirtyDaysAgoYmd());
  const [aiEnd, setAiEnd] = useState(todayYmd());
  const [aiPreamble, setAiPreamble] = useState('weekly_review');
  const [aiToggles, setAiToggles] = useState({
    include_reflection_titles: false,
    include_reflection_previews: false,
    include_reflection_full_text: false,
    include_habit_log_notes: false,
    include_time_entry_descriptions: false,
    include_project_task_descriptions: false,
    include_external_ids: false,
    include_source_metadata: false,
  });
  const [aiRedact, setAiRedact] = useState({ redact_emails: true, redact_urls: true, redact_phones: false });
  const [aiPasscode, setAiPasscode] = useState('');
  const [aiBusy, setAiBusy] = useState<'download' | 'copy' | null>(null);

  const toggleAi = (key: keyof typeof aiToggles) =>
    setAiToggles((prev) => ({ ...prev, [key]: !prev[key] }));

  const buildAiBody = () => ({
    format: aiFormat,
    range: aiRange,
    start_date: aiRange === 'custom' ? aiStart : undefined,
    end_date: aiRange === 'custom' ? aiEnd : undefined,
    tz_offset: new Date().getTimezoneOffset(),
    preamble: aiPreamble,
    ...aiToggles,
    ...aiRedact,
  });

  // Titles, previews, and full text are all passcode-protected server-side, so
  // any of them requires the passcode when one is set.
  const aiNeedsPasscode =
    aiToggles.include_reflection_titles ||
    aiToggles.include_reflection_previews ||
    aiToggles.include_reflection_full_text;
  const aiHeaders = (): Record<string, string> =>
    aiNeedsPasscode && aiPasscode ? { 'X-Reflection-Passcode': aiPasscode } : {};

  const handleAiDownload = async () => {
    setError(null);
    setInfo(null);
    setAiBusy('download');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/exports/ai-context`, buildAiBody(), {
        responseType: 'blob',
        headers: aiHeaders(),
      });
      const ext = aiFormat === 'markdown' ? 'md' : aiFormat;
      triggerBlobDownload(
        res.data,
        res.headers['content-type'] || 'text/plain',
        filenameFromDisposition(res.headers['content-disposition']) || `kalpa-ai-context-${todayYmd()}.${ext}`,
      );
      setInfo('AI context export downloaded.');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'AI context export failed.'));
    } finally {
      setAiBusy(null);
    }
  };

  const handleAiCopy = async () => {
    setError(null);
    setInfo(null);
    setAiBusy('copy');
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/exports/ai-context`,
        { ...buildAiBody(), format: 'markdown' },
        { responseType: 'text', headers: aiHeaders() },
      );
      await navigator.clipboard.writeText(res.data as unknown as string);
      setInfo('Markdown copied to clipboard.');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to copy AI context.'));
    } finally {
      setAiBusy(null);
    }
  };

  // ------------------------------------------------------------------ History
  const [batches, setBatches] = useState<BatchResponse[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState<number | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get<BatchResponse[]>(`${API_BASE_URL}/api/data-portability/import-batches`);
      setBatches(res.data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load import history.'));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 3) {
      loadHistory();
    }
  }, [tab, loadHistory]);

  const handleRollback = async (batchId: number) => {
    setError(null);
    setInfo(null);
    setRollingBack(batchId);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/data-portability/import-batches/${batchId}/rollback`,
      );
      const deleted = Object.values(res.data.deleted || {}).reduce((a: number, b: any) => a + Number(b), 0);
      setInfo(`Rolled back batch #${batchId} (${deleted} row(s) deleted).`);
      loadHistory();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Rollback failed.'));
    } finally {
      setRollingBack(null);
    }
  };

  // ------------------------------------------------------------------ render helpers
  const renderDataBackup = () => (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Data Backup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Download a machine-readable <code>kalpa-export.zip</code> (manifest + checksums + per-domain
          JSON) you can re-import later. This is a full backup, not a human-readable report.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Domains
        </Typography>
        <FormGroup row>
          {BACKUP_DOMAINS.map((d) => (
            <FormControlLabel
              key={d.key}
              control={
                <Checkbox
                  checked={!!selectedDomains[d.key]}
                  onChange={() => toggleDomain(d.key)}
                  inputProps={{ 'aria-label': d.label }}
                />
              }
              label={d.label}
              sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={pageGridSpacing} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="refl-mode-label">Reflections</InputLabel>
              <Select
                labelId="refl-mode-label"
                label="Reflections"
                value={reflectionMode}
                onChange={(e) => setReflectionMode(e.target.value as ReflectionMode)}
              >
                <MenuItem value="metadata_only">Metadata only (no content)</MenuItem>
                <MenuItem value="encrypted">Encrypted (restore needs same key)</MenuItem>
                <MenuItem value="plaintext">Plaintext (readable — sensitive)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeExternalIds}
                  onChange={() => setIncludeExternalIds((v) => !v)}
                />
              }
              label="Include external IDs"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeSourceMetadata}
                  onChange={() => setIncludeSourceMetadata((v) => !v)}
                />
              }
              label="Include source metadata"
            />
          </Grid>
        </Grid>

        {reflectionMode === 'plaintext' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Plaintext export writes your decrypted journal entries in the clear. Store the archive
            securely. If a reflection passcode is set, enter it below to authorize.
          </Alert>
        )}
        {selectedDomains.reflections && (
          <TextField
            type="password"
            size="small"
            label="Reflection passcode (if set)"
            helperText="Required to export reflections when you've set a passcode"
            value={backupPasscode}
            onChange={(e) => setBackupPasscode(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}
        {reflectionMode === 'encrypted' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Encrypted reflections are only restorable on a server with the same
            REFLECTION_ENCRYPTION_KEY. Back that key up separately.
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={backupBusy ? <CircularProgress size={18} /> : <FileDownloadIcon />}
            onClick={handleBackupDownload}
            disabled={backupBusy}
          >
            Download backup
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderImport = () => (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Import
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a <code>kalpa-export.zip</code>, preview the changes (no data is modified during
          preview), then apply. Apply is only enabled after a successful preview.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <input
            ref={importInputRef}
            type="file"
            accept=".zip,application/zip"
            data-testid="import-file-input"
            style={{ display: 'none' }}
            onChange={onSelectFile}
          />
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => importInputRef.current?.click()}
          >
            Choose backup file
          </Button>
          {importFile && <Chip label={importFile.name} onDelete={() => { setImportFile(null); setPreview(null); }} />}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="conflict-mode-label">Conflict mode</InputLabel>
            <Select
              labelId="conflict-mode-label"
              label="Conflict mode"
              value={conflictMode}
              onChange={(e) => { setConflictMode(e.target.value); setPreview(null); }}
            >
              <MenuItem value="skip">Skip existing</MenuItem>
              <MenuItem value="duplicate">Duplicate</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={runPreview}
            disabled={!importFile || importBusy !== null}
            startIcon={importBusy === 'preview' ? <CircularProgress size={18} /> : undefined}
            sx={{ mr: 2 }}
          >
            Preview import
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={runApply}
            disabled={!preview?.ok || importBusy !== null}
            startIcon={importBusy === 'apply' ? <CircularProgress size={18} /> : undefined}
          >
            Apply import
          </Button>
        </Box>

        {preview && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Preview — {preview.totals.to_create} to create, {preview.totals.to_skip} to skip,{' '}
              {preview.totals.conflicts} conflict(s), {preview.totals.invalid} invalid
            </Typography>
            {preview.dependency_warnings.map((w, i) => (
              <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                {w}
              </Alert>
            ))}
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell align="right">Read</TableCell>
                    <TableCell align="right">Create</TableCell>
                    <TableCell align="right">Skip</TableCell>
                    <TableCell align="right">Conflicts</TableCell>
                    <TableCell align="right">Invalid</TableCell>
                    <TableCell align="right">Orphaned refs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.domains.map((d) => (
                    <TableRow key={d.domain}>
                      <TableCell>{d.domain}</TableCell>
                      <TableCell align="right">{d.read}</TableCell>
                      <TableCell align="right">{d.to_create}</TableCell>
                      <TableCell align="right">{d.to_skip}</TableCell>
                      <TableCell align="right">{d.conflicts}</TableCell>
                      <TableCell align="right">{d.invalid}</TableCell>
                      <TableCell align="right">{d.orphaned_references}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const AI_TOGGLE_LABELS: Array<[keyof typeof aiToggles, string]> = [
    ['include_reflection_titles', 'Reflection titles'],
    ['include_reflection_previews', 'Reflection previews'],
    ['include_reflection_full_text', 'Reflection full text'],
    ['include_habit_log_notes', 'Habit-log notes'],
    ['include_time_entry_descriptions', 'Time-entry descriptions'],
    ['include_project_task_descriptions', 'Project/task descriptions'],
    ['include_external_ids', 'External IDs'],
    ['include_source_metadata', 'Source metadata'],
  ];

  const renderAiContext = () => (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          AI Context Export
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A privacy-filtered summary for pasting into ChatGPT, Claude, Gemini, or Grok. Everything
          content-bearing is excluded by default — turn on only what you want to share.
        </Typography>

        <Grid container spacing={pageGridSpacing}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ai-range-label">Date range</InputLabel>
              <Select
                labelId="ai-range-label"
                label="Date range"
                value={aiRange}
                onChange={(e) => setAiRange(e.target.value)}
              >
                {['7d', '30d', '90d', 'ytd', 'all', 'custom'].map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {aiRange === 'custom' && (
            <>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start"
                  InputLabelProps={{ shrink: true }}
                  value={aiStart}
                  onChange={(e) => setAiStart(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End"
                  InputLabelProps={{ shrink: true }}
                  value={aiEnd}
                  onChange={(e) => setAiEnd(e.target.value)}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ai-preamble-label">Preamble</InputLabel>
              <Select
                labelId="ai-preamble-label"
                label="Preamble"
                value={aiPreamble}
                onChange={(e) => setAiPreamble(e.target.value)}
              >
                <MenuItem value="career_coach">Career coach</MenuItem>
                <MenuItem value="weekly_review">Weekly review</MenuItem>
                <MenuItem value="habit_coach">Habit coach</MenuItem>
                <MenuItem value="planning_assistant">Planning assistant</MenuItem>
                <MenuItem value="none">No preamble</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="ai-format-label">Format</InputLabel>
              <Select
                labelId="ai-format-label"
                label="Format"
                value={aiFormat}
                onChange={(e) => setAiFormat(e.target.value)}
              >
                <MenuItem value="markdown">Markdown</MenuItem>
                <MenuItem value="txt">Plain text</MenuItem>
                <MenuItem value="jsonl">JSONL</MenuItem>
                <MenuItem value="zip">ZIP bundle</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Include (off by default)
        </Typography>
        <FormGroup row>
          {AI_TOGGLE_LABELS.map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch checked={aiToggles[key]} onChange={() => toggleAi(key)} inputProps={{ 'aria-label': label }} />
              }
              label={label}
              sx={{ width: { xs: '100%', sm: '45%', md: '30%' } }}
            />
          ))}
        </FormGroup>

        {aiNeedsPasscode && (
          <TextField
            type="password"
            size="small"
            label="Reflection passcode (if set)"
            value={aiPasscode}
            onChange={(e) => setAiPasscode(e.target.value)}
            sx={{ mt: 1, mb: 1 }}
          />
        )}

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Redaction
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={<Switch checked={aiRedact.redact_emails} onChange={() => setAiRedact((p) => ({ ...p, redact_emails: !p.redact_emails }))} />}
            label="Redact emails"
          />
          <FormControlLabel
            control={<Switch checked={aiRedact.redact_urls} onChange={() => setAiRedact((p) => ({ ...p, redact_urls: !p.redact_urls }))} />}
            label="Redact URLs"
          />
          <FormControlLabel
            control={<Switch checked={aiRedact.redact_phones} onChange={() => setAiRedact((p) => ({ ...p, redact_phones: !p.redact_phones }))} />}
            label="Redact phone numbers"
          />
        </FormGroup>

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={aiBusy === 'download' ? <CircularProgress size={18} /> : <FileDownloadIcon />}
            onClick={handleAiDownload}
            disabled={aiBusy !== null}
            sx={{ mr: 2 }}
          >
            Download
          </Button>
          <Button
            variant="outlined"
            startIcon={aiBusy === 'copy' ? <CircularProgress size={18} /> : <ContentCopyIcon />}
            onClick={handleAiCopy}
            disabled={aiBusy !== null}
          >
            Copy Markdown
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderHistory = () => (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Import History
        </Typography>
        {historyLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : batches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No imports yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
              <TableHead>
                <TableRow>
                  <TableCell>Batch</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Created</TableCell>
                  <TableCell align="right">Skipped</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>#{b.id}</TableCell>
                    <TableCell>
                      <Chip size="small" label={b.status} />
                    </TableCell>
                    <TableCell align="right">{b.total_created}</TableCell>
                    <TableCell align="right">{b.total_skipped}</TableCell>
                    <TableCell align="right">{b.total_failed}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        disabled={b.status === 'rolled_back' || rollingBack === b.id}
                        onClick={() => handleRollback(b.id)}
                      >
                        {rollingBack === b.id ? 'Rolling back…' : 'Roll back'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Typography variant="h3" component="h1">
          Exports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Back up your account, restore a backup, or generate a privacy-filtered summary for AI tools.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={sectionGapSx} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Box sx={[sectionGapSx, { borderBottom: 1, borderColor: 'divider' }]}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Data Backup" />
          <Tab label="Import" />
          <Tab label="AI Context Export" />
          <Tab label="Import History" />
        </Tabs>
      </Box>

      {tab === 0 && renderDataBackup()}
      {tab === 1 && renderImport()}
      {tab === 2 && renderAiContext()}
      {tab === 3 && renderHistory()}
    </PageShell>
  );
};

export default Exports;
